import { PageMeta } from 'constants/pages';
import { ArticleSections } from 'types/articles';
import { EHashtag, TCardProps } from 'types/common';
import amdSiloPartnershipImage from 'assets/partnerships/amd-silo/main-image.webp';

const title = 'Automating Useful Scenario Variation in Simulation at Scale on AMD Instinct™';

export const amdSiloPartnershipMeta: PageMeta = {
  url: '/partnerships/amd-silo',
  title,
  description:
    'AILiveSim and AMD Silo AI collaborate to advance AI-driven scenario generation and sensor simulation on AMD Instinct™ GPUs. The partnership enhances high-fidelity, multi-sensor simulation for maritime, defense, aerospace, and automotive industries, enabling faster, more realistic testing and training of autonomous and AI systems at scale.',
};

export const amdSiloPartnershipCard: TCardProps = {
  title,
  description:
    'AILiveSim and AMD Silo AI join forces to scale AI-driven scenario generation and sensor simulation. By combining AMD’s compute power with AILiveSim’s physics-accurate engine, they enable faster, more realistic testing for autonomous systems.',
  category: 'partnerships',
  type: 'Partnership',
  image: amdSiloPartnershipImage,
  postUrl: amdSiloPartnershipMeta.url,
  publishDate: 'November 6, 2025',
  needImageBg: true,
  hashTags: [
    EHashtag.SyntheticData,
    EHashtag.AITraining,
    EHashtag.AutonomousVehicles,
    EHashtag.AISimulation,
  ],
};

export const amdSiloPartnership: ArticleSections[] = [
  {
    type: 'header',
    resourceType: 'Partnership',
    title,
    text: (
      <>
        <p>
          <a href="https://ailivesim.com/" target="_blank" rel="noopener noreferrer">
            AILiveSim
          </a>{' '}
          is collaborating with{' '}
          <a href="http://amd.com/en" target="_blank" rel="noopener noreferrer">
            AMD Silo AI
          </a>{' '}
          to advance AI-driven scenario generation and sensor simulation workflows. This partnership
          combines AILiveSim’s high-fidelity, multi-sensor simulation expertise with AMD Silo AI’s
          deep AI and compute capabilities across maritime, defense, aerospace, and automotive
          domains.
        </p>
        <p>
          This collaboration unites AMD’s scalable compute platforms with AILiveSim’s photorealistic
          and physics-accurate simulation engine.Together, they enable industrial developers to
          create, test, and validate AI systems across multisensors —visual, infrared, radar, lidar,
          and inertial sensors — with unprecedented realism, control, and efficiency.
        </p>
        <p>
          “Working with AMD Silo AI allows us to scale our platform to meet the growing demand for
          high-performance, on-premise simulation,” said <strong>Jérôme Leudet</strong>, CEO of
          AILiveSim. “By leveraging AMD’s GPU technology and the ROCm™ software stack, we can
          deliver richer and faster digital-twin environments to our customers.”
        </p>
      </>
    ),
    image: amdSiloPartnershipImage,
  },
  {
    type: 'common',
    title: '',
    text: (
      <>
        <p>
          <strong>The collaboration will focus on:</strong>
        </p>

        <ul>
          <li>
            <strong>Optimizing AILiveSim’s software stack for AMD hardware and ROCm™</strong>,
            ensuring seamless performance across diverse compute configurations.
          </li>
          <li>
            <strong>Developing AI-assisted scenario generation pipelines</strong> that extract
            semantic intrinsic signatures from real-world data to retrieve, edit, and refine
            simulation assets from AILiveSim’s catalogue—minimizing the sim-to-real domain shift.
          </li>
          <li>
            <strong>Exploring generative AI and scene reconstruction techniques</strong> to automate
            the creation and adaptation of synthetic environments, improving fidelity and diversity
            for training and validation.
          </li>
        </ul>

        <p>
          AILiveSim’s engine provides full programmatic control over geometry, materials, lighting,
          and sensor configurations—producing highly detailed, physics-grounded environments ideal
          for testing perception and autonomy stacks in both open- and closed-loop scenarios.
        </p>

        <p>
          “Together with AILiveSim and our growing ecosystem of simulation software providers, we
          are narrowing the gap between real and simulated data,” said <strong>Niko Vuokko</strong>,
          Sr. Director at AMD Silo AI. AILiveSim’s state-of-the-art simulation technology and the
          leading-edge compute infrastructure of AMD, in combination, shorten customer development
          cycles while accelerating innovation”.
        </p>

        <p>
          This collaboration reinforces AMD Silo AI’s commitment to building an open and scalable
          simulation ecosystem—leveraging the full AMD compute stack from edge and digital-twin
          infrastructure to large-scale AI cloud compute.
        </p>

        <p>
          The joint work will also include open-source software releases, reinforcing both
          companies’ commitment to transparency, interoperability, and responsible AI innovation.
        </p>
      </>
    ),
  },
  {
    type: 'common',
    title: 'About AMD Silo AI',
    text: (
      <>
        <p>
          Pushing the frontier of AI on AMD compute platforms. AMD Silo AI is a leading AI lab that
          helps customers develop and deploy advanced AI models and solutions optimized for
          leadership compute platforms. As a global AI center of excellence with over 300 AI
          scientists and 125+ PhDs, we combine deep scientific knowledge with practical technology
          understanding to help organizations integrate, deploy and scale AI effectively. Our work
          spans from groundbreaking research to enterprise-ready AI solutions.
        </p>
      </>
    ),
  },
];
